import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Network, User, Mail, Phone, Lock, UserPlus, CheckCircle, ArrowRight } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function AgentRegister() {
  const navigate = useNavigate();
  const { referralCode } = useParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sponsor, setSponsor] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    referral_code: referralCode || ''
  });

  useEffect(() => {
    // Fetch sponsor details if referral code provided
    if (referralCode) {
      fetchSponsor(referralCode);
    }
  }, [referralCode]);

  const fetchSponsor = async (code) => {
    try {
      const response = await api.get(`/agent/sponsor/${code}`);
      setSponsor(response.data);
    } catch (error) {
      console.log('Sponsor not found');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/agent/register`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        sponsor_code: form.referral_code || null
      });
      
      setSuccess(true);
      toast.success('Registration successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Registration Successful!</h2>
            <p className="text-slate-600 mb-6">
              Your application has been submitted. Admin will review and approve your account shortly.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-amber-800">
                You will receive a confirmation once your account is approved. Then you can login and start earning!
              </p>
            </div>
            <Button 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={() => navigate('/agent-login')}
            >
              Go to Login <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Network className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Ganesa Partner Network</h1>
          <p className="text-slate-600">Join our team and start earning</p>
        </div>

        {/* Sponsor Card */}
        {sponsor && (
          <Card className="mb-4 border-emerald-200 bg-emerald-50/50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {sponsor.name?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="text-sm text-emerald-700">You're joining under</p>
                  <p className="font-semibold text-slate-800">{sponsor.name}</p>
                  <Badge className="bg-emerald-100 text-emerald-800 text-xs mt-1">{sponsor.rank}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Registration Form */}
        <Card className="shadow-xl border-0">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="text-emerald-600" />
              Agent Registration
            </CardTitle>
            <CardDescription>Fill in your details to join the network</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="flex items-center gap-2">
                  <User size={14} /> Full Name *
                </Label>
                <Input
                  placeholder="Enter your full name"
                  value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Mail size={14} /> Email *
                </Label>
                <Input
                  type="email"
                  placeholder="your.email@example.com"
                  value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Phone size={14} /> Phone Number *
                </Label>
                <Input
                  type="tel"
                  placeholder="9876543210"
                  value={form.phone}
                  onChange={e => setForm({...form, phone: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Lock size={14} /> Password *
                </Label>
                <Input
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Lock size={14} /> Confirm Password *
                </Label>
                <Input
                  type="password"
                  placeholder="Re-enter password"
                  value={form.confirmPassword}
                  onChange={e => setForm({...form, confirmPassword: e.target.value})}
                  required
                  className="mt-1"
                />
              </div>

              {!referralCode && (
                <div>
                  <Label className="flex items-center gap-2">
                    <Network size={14} /> Referral Code (Optional)
                  </Label>
                  <Input
                    placeholder="Enter sponsor's referral code"
                    value={form.referral_code}
                    onChange={e => {
                      setForm({...form, referral_code: e.target.value});
                      if (e.target.value.length >= 6) {
                        fetchSponsor(e.target.value);
                      } else {
                        setSponsor(null);
                      }
                    }}
                    className="mt-1 font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-1">If someone referred you, enter their code</p>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700 mt-6"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 w-4 h-4" />
                    Register as Agent
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t text-center">
              <p className="text-sm text-slate-600">
                Already registered?{' '}
                <Link to="/agent-login" className="text-emerald-600 font-semibold hover:underline">
                  Login here
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Benefits */}
        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
          <div className="bg-white/80 rounded-xl p-3">
            <p className="text-lg font-bold text-emerald-600">10 Levels</p>
            <p className="text-xs text-slate-600">Commission</p>
          </div>
          <div className="bg-white/80 rounded-xl p-3">
            <p className="text-lg font-bold text-emerald-600">Instant</p>
            <p className="text-xs text-slate-600">Withdrawal</p>
          </div>
          <div className="bg-white/80 rounded-xl p-3">
            <p className="text-lg font-bold text-emerald-600">Free</p>
            <p className="text-xs text-slate-600">Joining</p>
          </div>
        </div>
      </div>
    </div>
  );
}
